/**
 * Shared measurement model used by Estimates and Projects.
 * Location → Work Type / Feature → Measurements (area and deduction rectangles).
 * area_sqft is (length_in * width_in) / 144, matching estimate_room_sections.
 */

export const SURFACE_TYPE_OPTIONS = [
  { value: "floor", label: "Floor" },
  { value: "wall", label: "Wall" },
  { value: "backsplash", label: "Backsplash" },
  { value: "shower_wall", label: "Shower Wall" },
  { value: "shower_floor", label: "Shower Floor" },
  { value: "ceiling", label: "Ceiling" },
  { value: "countertop", label: "Countertop" },
  { value: "niche", label: "Niche" },
  { value: "other", label: "Other" },
];

export const RECORD_KIND_OPTIONS = [
  { value: "area", label: "Area" },
  { value: "deduction", label: "Deduction" },
];

export const DEDUCTION_TYPE_OPTIONS = [
  { value: "toilet", label: "Toilet footprint" },
  { value: "tub", label: "Tub footprint" },
  { value: "shower_pan", label: "Shower pan" },
  { value: "window", label: "Window opening" },
  { value: "door", label: "Door opening" },
  { value: "opening", label: "Opening" },
  { value: "cabinet", label: "Cabinet footprint" },
  { value: "island", label: "Island footprint" },
  { value: "other", label: "Other" },
];

const SURFACE_VALUES = new Set(SURFACE_TYPE_OPTIONS.map((option) => option.value));
const DEDUCTION_VALUES = new Set(
  DEDUCTION_TYPE_OPTIONS.map((option) => option.value),
);

export function formatSurfaceType(value) {
  const match = SURFACE_TYPE_OPTIONS.find((option) => option.value === value);
  return match?.label || value || "";
}

export function formatDeductionType(value) {
  const match = DEDUCTION_TYPE_OPTIONS.find((option) => option.value === value);
  return match?.label || "Deduction";
}

export function formatSurfaceTotals(surfaceTotals) {
  if (!surfaceTotals || typeof surfaceTotals !== "object") return [];

  return Object.entries(surfaceTotals).map(([surfaceType, sqft]) => ({
    surfaceType,
    label: formatSurfaceType(surfaceType),
    sqft: Number(sqft),
  }));
}

export function computeAreaSqft(lengthIn, widthIn) {
  const length = Number(lengthIn);
  const width = Number(widthIn);
  if (!length || !width || length <= 0 || width <= 0) return 0;
  return Math.round(((length * width) / 144) * 10000) / 10000;
}

/** How the user types a measurement. Inches-only entry uses Feet & Inches. */
export const MEASUREMENT_INPUT_UNITS = [
  { value: "ft-in", label: "Feet & Inches" },
  { value: "mm", label: "Millimetres" },
];

/** Area output only. It does not change length, width, or how those are shown. */
export const MEASUREMENT_RESULT_UNITS = [
  { value: "ft2", label: "Square Feet" },
  { value: "mm2", label: "Square Millimetres" },
];

const MM_PER_INCH = 25.4;

function roundInches(value) {
  return Math.round(Number(value) * 10000) / 10000;
}

function parseNonNegative(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return NaN;
  return parsed;
}

/**
 * Inch remainder stays a decimal (6.5) so a later fraction formatter can
 * render 6 1/2 without a storage change. length_in remains total inches.
 */
export function formatFeetAndInches(totalInches) {
  const total = Number(totalInches);
  if (!Number.isFinite(total)) return "";
  const negative = total < 0;
  let abs = Math.abs(total);
  let feet = Math.floor(abs / 12 + 1e-9);
  let inches = Math.round((abs - feet * 12) * 10000) / 10000;
  if (inches >= 12) {
    feet += 1;
    inches = 0;
  }
  const inchText = Number.isInteger(inches) ? String(inches) : String(inches);
  const text = `${feet}' ${inchText}"`;
  return negative ? `-${text}` : text;
}

export function inchesToMillimetres(totalInches) {
  const inches = Number(totalInches);
  if (!Number.isFinite(inches)) return NaN;
  return Math.round(inches * MM_PER_INCH);
}

export function millimetresToInches(millimetres) {
  const mm = Number(millimetres);
  if (!Number.isFinite(mm)) return NaN;
  return roundInches(mm / MM_PER_INCH);
}

/**
 * Millimetre text for an input that must convert back to the stored inches.
 * Display text uses the nearest millimetre instead.
 */
function inputMillimetresFromInches(totalInches) {
  const inches = roundInches(totalInches);
  const exact = inches * MM_PER_INCH;
  for (let places = 1; places <= 6; places += 1) {
    const factor = 10 ** places;
    const rounded = Math.round(exact * factor) / factor;
    if (millimetresToInches(rounded) === inches) return rounded;
  }
  return exact;
}

export function formatMillimetres(millimetres) {
  const mm = Number(millimetres);
  if (!Number.isFinite(mm)) return "";
  const rounded = Math.round(mm);
  return `${rounded} mm`;
}

export function formatStoredDimension(lengthIn, dimensionUnit) {
  if (dimensionUnit === "mm") {
    return formatMillimetres(inchesToMillimetres(lengthIn));
  }
  return formatFeetAndInches(lengthIn);
}

export function formatMeasurementDimensions(section) {
  const unit = section?.dimension_unit === "mm" ? "mm" : "in";
  return `${formatStoredDimension(section?.length_in, unit)} × ${formatStoredDimension(section?.width_in, unit)}`;
}

const SQ_MM_PER_SQ_INCH = MM_PER_INCH * MM_PER_INCH;

export function formatAreaSqft(areaSqft) {
  const area = Number(areaSqft);
  if (!Number.isFinite(area)) return "";
  const rounded = Math.round(area * 1000) / 1000;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return `${text} ft²`;
}

export function formatAreaSquareMillimetres(squareMm) {
  const area = Number(squareMm);
  if (!Number.isFinite(area)) return "";
  const rounded = Math.round(area * 10) / 10;
  const text = rounded.toLocaleString("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 1,
  });
  return `${text} mm²`;
}

/**
 * Square millimetres for display.
 * Imperial sizes use the stored inches. Millimetre sizes use the same
 * nearest-millimetre figures shown on the dimension line, so 1000 × 500
 * stays 500,000 mm². area_sqft is not rewritten.
 */
export function areaSquareMillimetres(lengthIn, widthIn, dimensionUnit) {
  if (dimensionUnit === "mm") {
    return inchesToMillimetres(lengthIn) * inchesToMillimetres(widthIn);
  }
  return Number(lengthIn) * Number(widthIn) * SQ_MM_PER_SQ_INCH;
}

export function formatMeasurementArea(section) {
  if (section?.area_unit === "mm2") {
    return formatAreaSquareMillimetres(
      areaSquareMillimetres(section?.length_in, section?.width_in, section?.dimension_unit),
    );
  }
  const sqft =
    section?.area_sqft != null && section.area_sqft !== ""
      ? Number(section.area_sqft)
      : computeAreaSqft(section?.length_in, section?.width_in);
  return formatAreaSqft(sqft);
}

function combineFeetAndInches(feet, inches) {
  const feetBlank = feet === "" || feet == null;
  const inchesBlank = inches === "" || inches == null;
  if (feetBlank && inchesBlank) return NaN;
  const ft = feetBlank ? 0 : parseNonNegative(feet);
  const inch = inchesBlank ? 0 : parseNonNegative(inches);
  if (Number.isNaN(ft) || Number.isNaN(inch)) return NaN;
  return ft * 12 + inch;
}

function inputNumber(value) {
  if (!Number.isFinite(value)) return "";
  const rounded = Math.round(value * 10000) / 10000;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export function splitFeetAndInches(totalInches) {
  const total = Number(totalInches);
  if (!Number.isFinite(total)) return { feet: "", inches: "" };
  const negative = total < 0;
  let abs = Math.abs(total);
  let feet = Math.floor(abs / 12 + 1e-9);
  let inches = Math.round((abs - feet * 12) * 10000) / 10000;
  if (inches >= 12) {
    feet += 1;
    inches = 0;
  }
  return {
    feet: inputNumber(negative ? -feet : feet),
    inches: inputNumber(inches),
  };
}

function storedDimensionUnit(section) {
  return entryMode(section) === "mm" ? "mm" : "in";
}

function storedAreaUnit(section) {
  if (section?.areaUnit === "mm2" || section?.area_unit === "mm2") return "mm2";
  return "ft2";
}

function entryMode(section) {
  if (section?.entryUnit === "mm") return "mm";
  if (section?.entryUnit === "ft-in") return "ft-in";
  if (section?.entryUnit === "in") return "in";
  const hasFeet =
    (section?.lengthFeet !== "" && section?.lengthFeet != null) ||
    (section?.lengthInches !== "" && section?.lengthInches != null) ||
    (section?.widthFeet !== "" && section?.widthFeet != null) ||
    (section?.widthInches !== "" && section?.widthInches != null);
  if (hasFeet) return "ft-in";
  return "in";
}

function fieldsFromInches(lengthIn, widthIn, entryUnit) {
  if (entryUnit === "mm") {
    return {
      lengthFeet: "",
      lengthInches: "",
      widthFeet: "",
      widthInches: "",
      lengthMm: inputNumber(inputMillimetresFromInches(lengthIn)),
      widthMm: inputNumber(inputMillimetresFromInches(widthIn)),
    };
  }
  const length = splitFeetAndInches(lengthIn);
  const width = splitFeetAndInches(widthIn);
  return {
    lengthFeet: length.feet,
    lengthInches: length.inches,
    widthFeet: width.feet,
    widthInches: width.inches,
    lengthMm: "",
    widthMm: "",
  };
}

function sameField(left, right) {
  return String(left ?? "") === String(right ?? "");
}

function matchesCanonical(section) {
  if (section?.canonicalLengthIn == null || section?.canonicalWidthIn == null) return false;
  const mode = entryMode(section) === "mm" ? "mm" : "ft-in";
  const expected = fieldsFromInches(section.canonicalLengthIn, section.canonicalWidthIn, mode);
  if (mode === "mm") {
    return (
      sameField(section.lengthMm, expected.lengthMm) &&
      sameField(section.widthMm, expected.widthMm)
    );
  }
  return (
    sameField(section.lengthFeet, expected.lengthFeet) &&
    sameField(section.lengthInches, expected.lengthInches) &&
    sameField(section.widthFeet, expected.widthFeet) &&
    sameField(section.widthInches, expected.widthInches)
  );
}

/**
 * Turn the selected input into canonical inches.
 * dimension_unit follows that input. The area result unit is separate.
 * Callers that only pass lengthIn/widthIn stay on inches.
 */
export function resolveMeasurementDimensions(section) {
  const mode = entryMode(section);
  let lengthIn;
  let widthIn;

  if (mode === "ft-in") {
    lengthIn = combineFeetAndInches(section?.lengthFeet, section?.lengthInches);
    widthIn = combineFeetAndInches(section?.widthFeet, section?.widthInches);
    if (!lengthIn || !widthIn || lengthIn <= 0 || widthIn <= 0 || Number.isNaN(lengthIn) || Number.isNaN(widthIn)) {
      throw new Error("Enter length and width.");
    }
  } else if (mode === "mm") {
    const lengthMm = parseNonNegative(section?.lengthMm);
    const widthMm = parseNonNegative(section?.widthMm);
    if (lengthMm == null || widthMm == null || !(lengthMm > 0) || !(widthMm > 0)) {
      throw new Error("Enter length and width in millimetres.");
    }
    lengthIn = millimetresToInches(lengthMm);
    widthIn = millimetresToInches(widthMm);
  } else {
    lengthIn = Number(section?.lengthIn);
    widthIn = Number(section?.widthIn);
    if (!lengthIn || !widthIn || lengthIn <= 0 || widthIn <= 0) {
      throw new Error("Enter length and width in inches.");
    }
  }

  if (matchesCanonical(section)) {
    lengthIn = Number(section.canonicalLengthIn);
    widthIn = Number(section.canonicalWidthIn);
  }

  return {
    lengthIn: roundInches(lengthIn),
    widthIn: roundInches(widthIn),
    dimensionUnit: storedDimensionUnit(section),
    areaUnit: storedAreaUnit(section),
  };
}

export function measurementEntryFromRow(section) {
  const entryUnit = section?.dimension_unit === "mm" ? "mm" : "ft-in";
  const lengthIn = Number(section?.length_in);
  const widthIn = Number(section?.width_in);
  return {
    entryUnit,
    areaUnit: section?.area_unit === "mm2" ? "mm2" : "ft2",
    canonicalLengthIn: lengthIn,
    canonicalWidthIn: widthIn,
    lengthIn: inputNumber(lengthIn),
    widthIn: inputNumber(widthIn),
    ...fieldsFromInches(lengthIn, widthIn, entryUnit),
  };
}

export function emptyMeasurementEntry() {
  return {
    entryUnit: "ft-in",
    areaUnit: "ft2",
    canonicalLengthIn: null,
    canonicalWidthIn: null,
    lengthIn: "",
    widthIn: "",
    lengthFeet: "",
    lengthInches: "",
    widthFeet: "",
    widthInches: "",
    lengthMm: "",
    widthMm: "",
  };
}

export function convertMeasurementEntry(form, nextUnit) {
  let lengthIn = null;
  let widthIn = null;
  if (matchesCanonical(form)) {
    lengthIn = Number(form.canonicalLengthIn);
    widthIn = Number(form.canonicalWidthIn);
  } else {
    try {
      const resolved = resolveMeasurementDimensions(form);
      lengthIn = resolved.lengthIn;
      widthIn = resolved.widthIn;
    } catch {
      lengthIn = null;
    }
  }
  const next = {
    ...form,
    entryUnit: nextUnit,
    lengthIn: "",
    widthIn: "",
    lengthFeet: "",
    lengthInches: "",
    widthFeet: "",
    widthInches: "",
    lengthMm: "",
    widthMm: "",
  };
  if (lengthIn == null) return next;
  return {
    ...next,
    lengthIn: inputNumber(lengthIn),
    widthIn: inputNumber(widthIn),
    ...fieldsFromInches(lengthIn, widthIn, nextUnit),
  };
}

/**
 * Column payload shared by estimate_room_sections and project_measurements.
 * Does not include parent ids or sort_order.
 */
export function buildMeasurementWritePayload(section) {
  const name = String(section?.name || "").trim();
  if (!name) {
    throw new Error("Enter a measurement name.");
  }

  const recordKind = section?.recordKind || "area";
  if (recordKind !== "area" && recordKind !== "deduction") {
    throw new Error("Measurement kind is not supported.");
  }

  const surfaceType = section?.surfaceType;
  if (!SURFACE_VALUES.has(surfaceType)) {
    throw new Error("Surface type is not supported.");
  }

  const surfaceLabel = String(section?.surfaceLabel || "").trim();
  if (surfaceType === "other" && !surfaceLabel) {
    throw new Error("Enter a label for other surface type.");
  }

  const dimensions = resolveMeasurementDimensions(section);

  const payload = {
    name,
    record_kind: recordKind,
    surface_type: surfaceType,
    surface_label: surfaceLabel || null,
    length_in: dimensions.lengthIn,
    width_in: dimensions.widthIn,
    dimension_unit: dimensions.dimensionUnit,
    area_unit: dimensions.areaUnit,
    notes: section?.notes ? String(section.notes) : null,
    deduction_type: null,
    applies_to_surface_type: null,
  };

  if (recordKind === "deduction") {
    if (!DEDUCTION_VALUES.has(section?.deductionType)) {
      throw new Error("Choose a deduction type.");
    }
    if (!SURFACE_VALUES.has(section?.appliesToSurfaceType)) {
      throw new Error("Choose the surface this deduction applies to.");
    }
    payload.deduction_type = section.deductionType;
    payload.applies_to_surface_type = section.appliesToSurfaceType;
    // Only the desktop editor sends this. Phone forms omit it, so a phone
    // edit does not clear a link (and does not write a column older
    // databases do not have).
    if (Object.prototype.hasOwnProperty.call(section, "appliesToSectionId")) {
      payload.applies_to_section_id = section.appliesToSectionId || null;
    }
  } else if (Object.prototype.hasOwnProperty.call(section, "appliesToSectionId")) {
    payload.applies_to_section_id = null;
  }

  return payload;
}

/** "2 ft 0 in" (or "610 mm") — explicit units for read-only displays. */
export function formatDimensionWithUnits(lengthIn, dimensionUnit) {
  if (dimensionUnit === "mm") return formatMillimetres(inchesToMillimetres(lengthIn));
  const { feet, inches } = splitFeetAndInches(lengthIn);
  if (feet === "" && inches === "") return "";
  return `${feet || 0} ft ${inches || 0} in`;
}

/** "2 ft 0 in × 2 ft 0 in", respecting the row's input unit. */
export function formatMeasurementDimensionsWithUnits(section) {
  const unit = section?.dimension_unit === "mm" ? "mm" : "in";
  const length = formatDimensionWithUnits(section?.length_in, unit);
  const width = formatDimensionWithUnits(section?.width_in, unit);
  if (!length || !width) return "";
  return `${length} × ${width}`;
}
